<template>
    <v-container class="px-5">
        <v-btn variant="text" size="small" prepend-icon="mdi-arrow-left" class="mb-2"
               :to="{ name: 'admin-app-installs' }">Back to app installs</v-btn>

        <v-skeleton-loader v-if="loading" type="card, table"/>

        <v-alert v-else-if="error" type="error" variant="tonal">{{ error }}</v-alert>

        <template v-else-if="install">
            <!-- Phone -->
            <v-card rounded="lg" variant="outlined" class="mb-4">
                <v-card-text>
                    <v-row align="center">
                        <v-col>
                            <div class="text-subtitle-1 font-weight-bold">
                                <v-icon size="20" class="mr-1">{{ platformIcon(install.platform) }}</v-icon>
                                {{ phoneType(install) }}
                            </div>
                            <div class="text-caption text-medium-emphasis">
                                Phone ID <code>{{ install.install_id }}</code>
                                <v-btn icon size="x-small" variant="text" @click="copyToClipboard(install.install_id)">
                                    <v-icon size="14">mdi-content-copy</v-icon>
                                </v-btn>
                                · changes if the app is reinstalled
                            </div>
                        </v-col>
                        <v-col cols="auto" class="d-flex ga-2 align-center">
                            <v-chip size="small" variant="tonal" :color="APPS[install.app]?.color">
                                {{ APPS[install.app]?.label || install.app }} {{ versionLabel(install) }}
                            </v-chip>
                            <v-chip size="small" variant="tonal" :color="STATUS[install.status]?.color">
                                {{ STATUS[install.status]?.label || install.status }}
                                <v-tooltip activator="parent" location="top">{{ STATUS[install.status]?.hint }}</v-tooltip>
                            </v-chip>
                        </v-col>
                    </v-row>

                    <v-table density="compact" class="mt-3">
                        <tbody>
                            <tr>
                                <td class="font-weight-medium" width="160">User</td>
                                <td>
                                    <template v-if="install.user">
                                        {{ install.user.name ? `${install.user.name} (${install.user.email})` : install.user.email }}
                                        <v-chip v-if="install.user.deleted" size="x-small" color="error" variant="tonal" class="ml-1">deleted</v-chip>
                                    </template>
                                    <span v-else>-</span>
                                </td>
                            </tr>
                            <tr>
                                <td class="font-weight-medium">Company</td>
                                <td>
                                    <template v-if="install.company">
                                        {{ install.company.name }}
                                        <v-chip v-if="install.company.deleted" size="x-small" color="error" variant="tonal" class="ml-1">deleted</v-chip>
                                    </template>
                                    <span v-else>-</span>
                                </td>
                            </tr>
                            <tr>
                                <td class="font-weight-medium">Installed</td>
                                <td>
                                    {{ formatDateTime(install.installed_at) }}
                                    <span v-if="!install.installed_at_reported" class="text-caption text-grey">
                                        (first seen; this app build does not report its install date)
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <td class="font-weight-medium">First seen</td>
                                <td>{{ formatDateTime(install.first_seen_at) }}</td>
                            </tr>
                            <tr>
                                <td class="font-weight-medium">Last seen</td>
                                <td>{{ formatDateTime(install.last_seen_at) }} ({{ relativeTime(install.last_seen_at) }})</td>
                            </tr>
                            <tr>
                                <td class="font-weight-medium">Last IP</td>
                                <td>
                                    <template v-if="install.last_ip">{{ install.last_ip }} ({{ relativeTime(install.last_ip_at) }})</template>
                                    <span v-else class="text-grey">Not reported yet</span>
                                </td>
                            </tr>
                            <tr>
                                <td class="font-weight-medium">Last used</td>
                                <td>
                                    {{ install.last_usage_date ? formatDate(install.last_usage_date + 'T12:00:00') : 'No usage reported yet' }}
                                    · used {{ install.used_days_7 }} of the last 7 days
                                </td>
                            </tr>
                        </tbody>
                    </v-table>
                </v-card-text>
            </v-card>

            <!-- Month usage -->
            <v-card rounded="lg" variant="outlined" class="mb-4">
                <v-card-title class="d-flex align-center flex-wrap ga-2">
                    <span class="text-subtitle-1 font-weight-bold">Daily usage</span>
                    <v-spacer/>
                    <v-btn icon size="small" variant="text" :disabled="!canGoBack || usageLoading" @click="shiftMonth(-1)">
                        <v-icon>mdi-chevron-left</v-icon>
                    </v-btn>
                    <span class="text-body-1 month-label">{{ monthLabel }}</span>
                    <v-btn icon size="small" variant="text" :disabled="!canGoForward || usageLoading" @click="shiftMonth(1)">
                        <v-icon>mdi-chevron-right</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text>
                    <v-progress-linear v-if="usageLoading" indeterminate color="primary" class="mb-2"/>
                    <template v-if="usage">
                        <div class="text-body-2 mb-3">
                            Used on <strong>{{ usage.totals.days_used }}</strong> of {{ usage.days.length }} days,
                            {{ formatMinutes(usage.totals.foreground_seconds) }} in total,
                            {{ usage.totals.opens }} open{{ usage.totals.opens === 1 ? '' : 's' }}.
                        </div>
                        <UsageMonthChart :days="usage.days"/>
                    </template>
                </v-card-text>
            </v-card>

            <v-row>
                <!-- Versions -->
                <v-col cols="12" md="6">
                    <v-card rounded="lg" variant="outlined" class="h-100">
                        <v-card-title class="text-subtitle-1 font-weight-bold">Versions</v-card-title>
                        <v-table density="compact">
                            <thead>
                                <tr><th>Version</th><th>First seen</th><th>Last seen</th></tr>
                            </thead>
                            <tbody>
                                <tr v-for="v in install.versions" :key="`${v.app_version}-${v.app_build}`">
                                    <td>
                                        {{ versionLabel(v) }}
                                        <v-chip v-if="v.current" size="x-small" color="success" variant="tonal" class="ml-1">current</v-chip>
                                    </td>
                                    <td>{{ formatDate(v.first_seen_at) }}</td>
                                    <td>{{ formatDate(v.last_seen_at) }}</td>
                                </tr>
                                <tr v-if="!install.versions.length">
                                    <td colspan="3" class="text-grey">No versions recorded</td>
                                </tr>
                            </tbody>
                        </v-table>
                    </v-card>
                </v-col>

                <!-- Other users -->
                <v-col cols="12" md="6">
                    <v-card rounded="lg" variant="outlined" class="h-100">
                        <v-card-title class="text-subtitle-1 font-weight-bold">Other users on this phone</v-card-title>
                        <v-table density="compact">
                            <thead>
                                <tr><th>User</th><th>Company</th><th>Last seen</th><th>Status</th></tr>
                            </thead>
                            <tbody>
                                <tr v-for="other in install.other_users" :key="other.id" class="row-link"
                                    @click="router.push({name: 'admin-app-install', params: {id: other.id}})">
                                    <td>{{ other.user?.email || '-' }}</td>
                                    <td>{{ other.company?.name || '-' }}</td>
                                    <td>{{ relativeTime(other.last_seen_at) }}</td>
                                    <td>
                                        <v-chip size="x-small" variant="tonal" :color="STATUS[other.status]?.color">
                                            {{ STATUS[other.status]?.label || other.status }}
                                        </v-chip>
                                    </td>
                                </tr>
                                <tr v-if="!install.other_users.length">
                                    <td colspan="4" class="text-grey">Only this user has signed in on this phone</td>
                                </tr>
                            </tbody>
                        </v-table>
                    </v-card>
                </v-col>
            </v-row>
        </template>
    </v-container>
</template>

<script setup>
import {ref, computed, watch} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import {send} from '@/composables/apiClient'
import {useClipboard} from '@/composables/clipboard'
import {useSnackbarStore} from '@/stores/snackBarStore'
import UsageMonthChart from '@/components/cards/appInstalls/UsageMonthChart.vue'
import {
    STATUS, APPS, platformIcon, phoneType, versionLabel,
    formatDate, formatDateTime, relativeTime, formatMinutes,
} from '@/components/cards/appInstalls/appInstallFormat'

const route = useRoute()
const router = useRouter()
const {copyToClipboard} = useClipboard()
const snackbar = useSnackbarStore()

const loading = ref(true)
const error = ref(null)
const install = ref(null)
const usage = ref(null)
const usageLoading = ref(false)

// Null until the first usage load: the server picks the current month, so a
// browser in another timezone cannot open a month before the install.
const month = ref(null)

const monthLabel = computed(() => {
    if (!month.value) return ''
    const [y, m] = month.value.split('-').map(Number)
    return new Date(y, m - 1, 1).toLocaleDateString('en-US', {month: 'long', year: 'numeric'})
})
// YYYY-MM strings compare correctly as text.
const canGoBack = computed(() => !!usage.value && month.value > usage.value.first_month)
const canGoForward = computed(() => !!usage.value && month.value < usage.value.last_month)

const shiftMonth = (delta) => {
    const [y, m] = month.value.split('-').map(Number)
    const d = new Date(y, m - 1 + delta, 1)
    month.value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    fetchUsage()
}

let usageSeq = 0
const fetchUsage = () => {
    const seq = ++usageSeq
    usageLoading.value = true
    send('get', `admin/app-installs/${route.params.id}/usage`, month.value ? {month: month.value} : {})
        .then((response) => {
            if (seq !== usageSeq) return
            usage.value = response.data.data
            month.value = usage.value.month
        })
        .catch((e) => {
            if (seq !== usageSeq) return
            usage.value = null
            snackbar.showSnackbar(e.response?.data?.message || 'Failed to load usage', 'error')
        })
        .finally(() => {
            if (seq === usageSeq) usageLoading.value = false
        })
}

let installSeq = 0
const fetchInstall = () => {
    const seq = ++installSeq
    // Drop any usage response still in flight for the previous install.
    usageSeq++
    usageLoading.value = false
    loading.value = true
    error.value = null
    install.value = null
    usage.value = null
    month.value = null
    send('get', `admin/app-installs/${route.params.id}`)
        .then((response) => {
            if (seq !== installSeq) return
            install.value = response.data.data
            fetchUsage()
        })
        .catch((e) => {
            if (seq === installSeq) error.value = e.response?.data?.message || 'Failed to load this install'
        })
        .finally(() => {
            if (seq === installSeq) loading.value = false
        })
}

// Other users on the same phone open on this same page.
watch(() => route.params.id, (id) => {
    if (id) fetchInstall()
}, {immediate: true})
</script>

<style scoped>
.month-label {
    min-width: 140px;
    text-align: center;
}
.row-link {
    cursor: pointer;
}
</style>
